import React, { ChangeEvent, FormEvent, useState } from 'react';
import { Alert, Button, Typography } from '@mui/material';
import { cloneDeep } from 'lodash-es';
import { Form } from 'pages/AuthPage/AuthPage.styles';
import {
  validConfirmPassword,
  validPassword,
} from 'utils/helpers/formValidators';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import PasswordTextField from 'atoms/PasswordTextField/PasswordTextField';
import {
  PASSWORD_CONFIRM_ERROR_MESSAGE,
  PASSWORD_ERROR_MESSAGE,
} from 'utils/constants/form';
import { setIsPasswordChangeRequired } from 'store/user/userSlice';
import { useBaoDispatch } from 'utils/hooks/redux';
import { useWindowDimensions } from 'pages/AuthPage/AuthPage';
import Link from 'atoms/Link/Link';
import { palette } from 'utils/styles/variables';

const ForcePasswordChangeForm = () => {
  const { width } = useWindowDimensions();
  const dispatch = useBaoDispatch();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    oldPassword: { value: '', error: '' },
    password: { value: '', error: '' },
    confirmPassword: { value: '', error: '' },
  });
  const [apiError, setApiError] = useState('');
  const [isFinished, setIsFinished] = useState(false);

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const updatedForm = cloneDeep(form);

    if (!updatedForm.oldPassword.value) {
      updatedForm.oldPassword.error = 'Please enter your current password';
    }
    if (!validPassword(updatedForm.password.value)) {
      updatedForm.password.error = PASSWORD_ERROR_MESSAGE;
    }
    if (
      !validConfirmPassword(
        updatedForm.password.value,
        updatedForm.confirmPassword.value
      )
    ) {
      updatedForm.confirmPassword.error = PASSWORD_CONFIRM_ERROR_MESSAGE;
    }
    if (
      updatedForm.oldPassword.value &&
      updatedForm.password.value &&
      updatedForm.oldPassword.value === updatedForm.password.value
    ) {
      updatedForm.password.error =
        'New password must be different from your current password';
    }

    if (
      Boolean(updatedForm.oldPassword.error) ||
      Boolean(updatedForm.password.error) ||
      Boolean(updatedForm.confirmPassword.error)
    ) {
      setForm(updatedForm);
      return;
    }

    try {
      await axios.patch(
        `${process.env.REACT_APP_USER_SERVICE}/change-password`,
        {
          old_password: form.oldPassword.value,
          password: form.password.value,
        }
      );
      setApiError('');
      setIsFinished(true);
      dispatch(setIsPasswordChangeRequired(false));
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (err) {
      setApiError(err?.response?.data?.message || 'An error occurred');
    }
  };

  const handleChange = (
    e: ChangeEvent<HTMLInputElement>,
    field: keyof typeof form
  ) => {
    const { value } = e.target;
    setForm((prev) => {
      const next = cloneDeep(prev);
      next[field].value = value;
      if (field === 'oldPassword' && value) {
        next.oldPassword.error = '';
      }
      if (field === 'password') {
        if (validPassword(value)) next.password.error = '';
        if (validConfirmPassword(value, next.confirmPassword.value)) {
          next.confirmPassword.error = '';
        }
        if (
          value &&
          next.oldPassword.value &&
          value === next.oldPassword.value
        ) {
          next.password.error =
            'New password must be different from your current password';
        }
      }
      if (
        field === 'confirmPassword' &&
        validConfirmPassword(next.password.value, value)
      ) {
        next.confirmPassword.error = '';
      }
      return next;
    });
  };

  return (
    <Form noValidate onSubmit={handleSubmit}>
      {isFinished && (
        <Alert severity="success">
          Password updated successfully! Redirecting…
        </Alert>
      )}
      {apiError && (
        <Alert severity="error">{apiError.replaceAll('_', ' ')}</Alert>
      )}
      <PasswordTextField
        autoComplete="current-password"
        fullWidth
        variant="outlined"
        label="Current Password *"
        margin="normal"
        error={!!form.oldPassword.error}
        helperText={form.oldPassword.error}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          handleChange(e, 'oldPassword')
        }
        InputLabelProps={{ shrink: true }}
      />
      <PasswordTextField
        autoComplete="new-password"
        fullWidth
        variant="outlined"
        label="New Password *"
        margin="normal"
        error={!!form.password.error}
        helperText={form.password.error}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          handleChange(e, 'password')
        }
        InputLabelProps={{ shrink: true }}
      />
      <PasswordTextField
        autoComplete="new-password"
        fullWidth
        variant="outlined"
        label="Confirm New Password *"
        margin="normal"
        error={!!form.confirmPassword.error}
        helperText={form.confirmPassword.error}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          handleChange(e, 'confirmPassword')
        }
        InputLabelProps={{ shrink: true }}
      />
      <Button
        fullWidth
        variant="contained"
        type="submit"
        sx={{
          mt: '24px',
          lineHeight: '20px',
          textTransform: 'uppercase',
          borderRadius: '3px',
        }}
      >
        Update Password
      </Button>

      {width < 600 && (
        <Typography
          textAlign="right"
          variant="body1"
          fontSize="0.875rem"
          color="#999999"
          className="form-footer"
        >
          Back to{' '}
          <Link to="/signin" $color={palette.primary.main}>
            Sign in
          </Link>
        </Typography>
      )}
    </Form>
  );
};

export default ForcePasswordChangeForm;
